import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  captureSessionTokenFromHash,
  clearCourseActivityLaunchToken,
  getCourseActivityLaunchToken,
} from '../services/sessionAuth';
import {
  CourseActivityAttempt,
  CourseActivityMessage,
  clearRememberedCourseActivityAttemptId,
  getRememberedCourseActivityAttemptId,
  rememberCourseActivityAttemptId,
  startCourseActivity,
  startCourseActivityConversation,
} from '../services/courseActivity';
import {
  navigateWithSession,
} from '../services/sessionNavigation';
import {
  useCourseActivitySession,
} from '../hooks/useCourseActivitySession';
import RetziaActivityWorkspace from './retzia/RetziaActivityWorkspace';

type ScreenState =
  | 'starting'
  | 'ready'
  | 'error';

interface ReadyCourseActivityProps {
  initialAttempt: CourseActivityAttempt;
  initialMessages: CourseActivityMessage[];
}

const INTERACTION_MODE_LABELS: Record<
  'speaking' | 'writing' | 'mixed',
  string
> = {
  speaking: 'Expresión oral',
  writing: 'Escritura',
  mixed: 'Práctica mixta',
};

const interactionModeLabel = (
  _language: string,
  mode: 'speaking' | 'writing' | 'mixed'
): string => INTERACTION_MODE_LABELS[mode];

type DeliveryStatus =
  | 'idle'
  | 'success'
  | 'error';

interface ForumDeliveryPanelProps {
  attempt: CourseActivityAttempt;
  text: string;
  audioUrl?: string;
  waiting: boolean;
}

const copyDeliveryText = async (
  text: string
): Promise<boolean> => {
  try {
    if (
      navigator.clipboard &&
      window.isSecureContext
    ) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Continue with the compatibility fallback below.
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  textarea.style.pointerEvents = 'none';
  document.body.appendChild(textarea);
  textarea.select();

  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    textarea.remove();
  }
};

function ForumDeliveryPanel({
  attempt,
  text,
  audioUrl,
  waiting,
}: ForumDeliveryPanelProps) {
  const needsAudio =
    attempt.submission_mode === 'text_audio';

  const [copyStatus, setCopyStatus] =
    useState<DeliveryStatus>('idle');

  const [audioStatus, setAudioStatus] =
    useState<DeliveryStatus>('idle');

  const [prepared, setPrepared] =
    useState(false);

  const announcedRef = useRef(false);
  const panelRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setCopyStatus('idle');
    setAudioStatus('idle');
    setPrepared(false);
    announcedRef.current = false;
  }, [attempt.id, text, audioUrl]);

  const getSourceOrigin = (): string | null => {
    try {
      const url = new URL(attempt.source_origin);

      return url.protocol === 'https:'
        ? url.origin
        : null;
    } catch {
      return null;
    }
  };

  const notifyParent = (
    type:
      | 'retzia:forum-delivery-ready'
      | 'retzia:forum-return'
  ) => {
    const sourceOrigin = getSourceOrigin();

    if (!sourceOrigin || window.parent === window) {
      return false;
    }

    window.parent.postMessage(
      {
        type,
        source_cmid: attempt.source_cmid,
      },
      sourceOrigin
    );

    return true;
  };

  useEffect(() => {
    const isReady =
      copyStatus === 'success' &&
      (!needsAudio || audioStatus === 'success');

    if (!isReady || announcedRef.current) {
      return;
    }

    announcedRef.current = true;
    setPrepared(true);
    notifyParent('retzia:forum-delivery-ready');
    // notifyParent is intentionally derived from the immutable attempt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyStatus, audioStatus, needsAudio]);

  const downloadAudio = (): boolean => {
    if (!audioUrl) {
      return false;
    }

    try {
      const anchor = document.createElement('a');
      const safeActivityId = attempt.activity_id.replace(
        /[^a-z0-9_-]+/gi,
        '-'
      );

      anchor.href = audioUrl;
      anchor.download =
        `retzia-${safeActivityId}-respuesta-final.mp3`;
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      return true;
    } catch {
      return false;
    }
  };

  const prepareDelivery = async () => {
    setCopyStatus('idle');

    if (needsAudio) {
      setAudioStatus(
        downloadAudio()
          ? 'success'
          : 'error'
      );
    }

    setCopyStatus(
      await copyDeliveryText(text)
        ? 'success'
        : 'error'
    );
  };

  const retryCopy = async () => {
    setCopyStatus(
      await copyDeliveryText(text)
        ? 'success'
        : 'error'
    );
  };

  const retryAudio = () => {
    setAudioStatus(
      downloadAudio()
        ? 'success'
        : 'error'
    );
  };

  const returnToForum = () => {
    if (!notifyParent('retzia:forum-return')) {
      navigateWithSession('/home');
    }
  };

  const hasText = text.trim() !== '';
  const hasRequiredAudio = !needsAudio || Boolean(audioUrl);
  const canPrepare =
    hasText &&
    hasRequiredAudio &&
    attempt.delivery_available &&
    !waiting;

  useEffect(() => {
    if (!canPrepare) {
      return;
    }

    const timer = window.setTimeout(() => {
      panelRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }, 180);

    return () => window.clearTimeout(timer);
  }, [canPrepare]);

  return (
    <section
      ref={panelRef}
      className="course-activity-delivery"
      aria-labelledby="course-activity-delivery-title"
    >
      <div className="course-activity-delivery-step">
        Entrega en RETZ
      </div>

      <h2 id="course-activity-delivery-title">
        Prepara tu entrega
      </h2>

      <p>
        {needsAudio
          ? 'Cuando RETZIA te pida tu respuesta final, respóndela con el micrófono. Prepararemos el texto y el audio para que los publiques en el foro.'
          : 'Cuando RETZIA te pida tu respuesta final, envíala aquí. Prepararemos el texto para que lo publiques en el foro.'}
      </p>

      {hasText && needsAudio && !audioUrl && (
        <div className="course-activity-delivery-note">
          Tu última respuesta fue escrita. Vuelve a decirla con el micrófono para incluir el audio.
        </div>
      )}

      <button
        type="button"
        className="course-activity-delivery-primary"
        disabled={!canPrepare}
        onClick={() => void prepareDelivery()}
      >
        Preparar mi entrega
      </button>

      {!hasText && (
        <span className="course-activity-delivery-hint">
          Primero completa la práctica con RETZIA.
        </span>
      )}

      {hasText && !attempt.delivery_available && (
        <span className="course-activity-delivery-hint">
          Continúa la conversación hasta que RETZIA te pida tu respuesta final.
        </span>
      )}

      {hasText && needsAudio && !audioUrl && (
        <button
          type="button"
          className="course-activity-delivery-fallback"
          onClick={returnToForum}
        >
          Grabarla después en el foro
        </button>
      )}

      {(copyStatus !== 'idle' ||
        audioStatus !== 'idle') && (
        <div
          className="course-activity-delivery-results"
          aria-live="polite"
        >
          <div
            className={`course-activity-delivery-result is-${copyStatus}`}
          >
            <span aria-hidden="true">
              {copyStatus === 'success' ? '✓' : '!'}
            </span>
            <strong>
              {copyStatus === 'success'
                ? 'Texto copiado'
                : 'No se pudo copiar el texto'}
            </strong>
            <button
              type="button"
              onClick={() => void retryCopy()}
            >
              Volver a copiar texto
            </button>
          </div>

          {needsAudio && (
            <div
              className={`course-activity-delivery-result is-${audioStatus}`}
            >
              <span aria-hidden="true">
                {audioStatus === 'success' ? '✓' : '!'}
              </span>
              <strong>
                {audioStatus === 'success'
                  ? 'Descarga del audio iniciada'
                  : 'No se pudo iniciar la descarga'}
              </strong>
              <button
                type="button"
                onClick={retryAudio}
              >
                Descargar audio nuevamente
              </button>
            </div>
          )}
        </div>
      )}

      {prepared && (
        <div className="course-activity-delivery-ready">
          <div>
            <strong>Tu entrega está lista.</strong>
            <span>
              Ahora publícala en el foro para que quede registrada en RETZ.
            </span>
          </div>

          <button
            type="button"
            onClick={returnToForum}
          >
            Volver al foro
          </button>
        </div>
      )}
    </section>
  );
}

function ReadyCourseActivity({
  initialAttempt,
  initialMessages,
}: ReadyCourseActivityProps) {
  const [attempt, setAttempt] =
    useState(initialAttempt);

  const session =
    useCourseActivitySession({
      attempt,
      initialMessages,
      onAttemptChange: setAttempt,
    });

  const modeLabel = interactionModeLabel(
    attempt.language,
    attempt.interaction_mode
  );

  const lastUserIndex = session.messages.reduce(
    (current, message, index) =>
      message.role === 'user'
        ? index
        : current,
    -1
  );

  const lastUserText = lastUserIndex >= 0
    ? session.messages[lastUserIndex].content
    : '';

  const lastUserAudioUrl = lastUserIndex >= 0
    ? session.getUserAudioUrl(lastUserIndex)
    : undefined;

  // submission_mode is a defensive compatibility signal for forum attempts
  // created while older frontend assets were still cached.
  const isForumActivity =
    attempt.source_kind === 'forum' ||
    attempt.submission_mode !== 'none';

  return (
    <RetziaActivityWorkspace
      language={attempt.language}
      session={session}
      reportMode="task_review"
      pageClassName="course-activity-workspace"
      inputPlaceholder="Escribe aquí…"
      afterConversation={
        isForumActivity ? (
          <ForumDeliveryPanel
            attempt={attempt}
            text={lastUserText}
            audioUrl={lastUserAudioUrl}
            waiting={
              session.isWaitingForBot ||
              session.isRecording ||
              session.isWaiting
            }
          />
        ) : undefined
      }
      onClose={() => {
        // Leaving the Course Activity view must not destroy an in-progress
        // attempt. Practice with RETZIA can resume it later.
        navigateWithSession('/home');
      }}
      header={
        <div className="course-activity-context-card">
          <div className="course-activity-context-meta">
            <span className="course-activity-context-pill course-activity-context-pill-kind">
              {isForumActivity
                ? 'Actividad de foro'
                : 'Actividad del curso'}
            </span>

            {modeLabel && (
              <span className="course-activity-context-pill course-activity-context-pill-mode">
                <span
                  className={`course-activity-mode-icon course-activity-mode-icon-${attempt.interaction_mode}`}
                  aria-hidden="true"
                />
                {modeLabel}
              </span>
            )}

            <span className="course-activity-context-pill course-activity-context-pill-status">
              <span className="course-activity-progress-dot" />
              {attempt.status === 'completed'
                ? 'Completada'
                : 'En progreso'}
            </span>
          </div>

          <h1>
            {attempt.title}
          </h1>

          <p>
            {attempt.objective}
          </p>
        </div>
      }
    />
  );
}

function CourseActivity() {
  const [screenState, setScreenState] =
    useState<ScreenState>('starting');

  const [attempt, setAttempt] =
    useState<CourseActivityAttempt | null>(
      null
    );

  const [messages, setMessages] =
    useState<CourseActivityMessage[]>([]);

  const [errorMessage, setErrorMessage] =
    useState('');

  const [retryNonce, setRetryNonce] =
    useState(0);

  useEffect(() => {
    let cancelled = false;

    const initialise = async () => {
      setScreenState('starting');
      setErrorMessage('');

      captureSessionTokenFromHash();

      try {
        const launchToken =
          getCourseActivityLaunchToken();

        let attemptId =
          getRememberedCourseActivityAttemptId();

        if (launchToken) {
          const createdAttempt =
            await startCourseActivity(
              launchToken
            );

          if (cancelled) {
            return;
          }

          attemptId =
            createdAttempt.id;

          rememberCourseActivityAttemptId(
            createdAttempt.id
          );

          clearCourseActivityLaunchToken();
        }

        if (!attemptId) {
          throw new Error(
            'No encontramos una actividad de curso para iniciar.'
          );
        }

        const conversation =
          await startCourseActivityConversation(
            attemptId
          );

        if (cancelled) {
          return;
        }

        rememberCourseActivityAttemptId(
          conversation.attempt.id
        );

        setAttempt(
          conversation.attempt
        );

        setMessages(
          conversation.messages
        );

        setScreenState('ready');
      } catch (error) {
        console.error(
          'Unable to start Course Activity.',
          error
        );

        if (cancelled) {
          return;
        }

        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'No fue posible iniciar la actividad.'
        );

        setScreenState('error');
      }
    };

    void initialise();

    return () => {
      cancelled = true;
    };
  }, [retryNonce]);

  if (
    screenState === 'ready' &&
    attempt
  ) {
    return (
      <ReadyCourseActivity
        initialAttempt={attempt}
        initialMessages={messages}
      />
    );
  }

  return (
    <div className="course-activity-loading-page">
      <div className="course-activity-loading-shell">
        <div className="course-activity-loading-brand">
          <span
            className="course-activity-context-icon"
            aria-hidden="true"
          >
            ✦
          </span>

          <span>Actividad del curso</span>
        </div>

        {screenState === 'starting' ? (
          <div className="course-activity-loading-state">
            <CircularProgress
              size={38}
              sx={{
                color: '#5b55df',
              }}
            />

            <p>
              Preparando tu actividad…
            </p>
          </div>
        ) : (
          <div className="course-activity-error-card">
            <h2>
              No pudimos abrir esta actividad.
            </h2>

            <p>
              {errorMessage}
            </p>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.2,
                marginTop: 2.5,
              }}
            >
              <Button
                onClick={() =>
                  setRetryNonce(
                    value => value + 1
                  )
                }
                variant="contained"
                sx={{
                  borderRadius: '999px',
                  background: '#5b55df',
                  fontWeight: 800,
                  textTransform: 'none',
                  '&:hover': {
                    background: '#4841c6',
                  },
                }}
              >
                Intentar de nuevo
              </Button>

              <Button
                onClick={() => {
                  clearRememberedCourseActivityAttemptId();
                  navigateWithSession('/home');
                }}
                variant="text"
                sx={{
                  color: '#5b55df',
                  fontWeight: 800,
                  textTransform: 'none',
                }}
              >
                Volver
              </Button>
            </Box>
          </div>
        )}
      </div>
    </div>
  );
}

export default CourseActivity;
